import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Image as ImageIcon, Bold, Italic, Underline, Tag } from 'lucide-react';
import JoditEditor from 'jodit-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const AddCardPage = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('0.00');
  const [isFreeForMembers, setIsFreeForMembers] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const fileInputRef = useRef(null);
  const editor = useRef(null);

  // Jodit Editor configuration
  const config = {
    readonly: false,
    placeholder: 'Write your card description here...',
    buttons: ['bold', 'italic', 'underline', 'link', 'ul', 'ol', 'font', 'fontsize', 'brush', 'paragraph'],
    height: 300,
    theme: 'default'
  };

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('access_token');
  };

  // Show Success Modal
  const showSuccessModal = () => {
    MySwal.fire({
      title: <strong>Success!</strong>,
      html: <p>Card added successfully!</p>,
      icon: 'success',
      iconColor: '#10B981',
      confirmButtonColor: '#10B981',
      confirmButtonText: 'Go to Dashboard',
      showClass: {
        popup: 'animate__animated animate__fadeInDown'
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOutUp'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/dla-shop');
      }
    });
  };

  // Show Error Modal
  const showErrorModal = (errorMessage) => {
    MySwal.fire({
      title: <strong>Error!</strong>,
      html: <p>{errorMessage}</p>,
      icon: 'error',
      iconColor: '#EF4444',
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Try Again',
      showClass: {
        popup: 'animate__animated animate__shakeX'
      }
    });
  };

  // Show Confirm Modal
  const showConfirmModal = () => {
    return MySwal.fire({
      title: <strong>Confirm</strong>,
      html: <p>Are you sure you want to add this card?</p>,
      icon: 'question',
      iconColor: '#3B82F6',
      showCancelButton: true,
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#EF4444',
      confirmButtonText: 'Yes, add it!',
      cancelButtonText: 'Cancel',
      showClass: {
        popup: 'animate__animated animate__fadeInDown'
      }
    });
  };

  // Show Loading Modal
  const showLoadingModal = () => {
    MySwal.fire({
      title: <strong>Adding Card...</strong>,
      html: <p>Please wait while we save your card.</p>,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      willOpen: () => {
        MySwal.showLoading();
      }
    });
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        MySwal.fire({
          title: <strong>File Too Large!</strong>,
          html: <p>File size should be less than 5MB</p>,
          icon: 'warning',
          iconColor: '#F59E0B',
          confirmButtonColor: '#F59E0B'
        });
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Remove selected image with confirmation
  const removeImage = () => {
    MySwal.fire({
      title: <strong>Remove Image?</strong>,
      html: <p>Are you sure you want to remove this image?</p>,
      icon: 'warning',
      iconColor: '#F59E0B',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, remove it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedImage(null);
        setImagePreview(null);
        MySwal.fire({
          title: <strong>Removed!</strong>,
          html: <p>Image has been removed.</p>,
          icon: 'success',
          iconColor: '#10B981',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  // Handle price change
  const handlePriceChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  // Create new card API call
  const createCard = async (formData) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('Please login first');
      }
      
      const response = await fetch('https://backend.dlapakistan.com/api/dla-shop/cards/create/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      // Get response text first to check if it's HTML error page
      const responseText = await response.text();
      
      if (!response.ok) {
        // Try to parse as JSON, if fails show text error
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.detail || errorData.error || `Server error: ${response.status}`);
        } catch (jsonError) {
          // If it's HTML (Django debug page), show generic error
          if (responseText.includes('<!DOCTYPE') || responseText.includes('<html')) {
            throw new Error(`Database table not created. Please run migrations: python manage.py migrate`);
          }
          throw new Error(responseText || `Server error: ${response.status}`);
        }
      }

      return JSON.parse(responseText);
    } catch (error) {
      console.error('Error creating card:', error);
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!title.trim()) {
      showErrorModal('Please enter card title');
      return;
    }
    
    if (!description.trim() || description === '<p><br></p>') {
      showErrorModal('Please enter card description');
      return;
    }

    // Validate price
    const priceValue = parseFloat(price);
    if (isNaN(priceValue) || priceValue < 0) {
      showErrorModal('Please enter a valid price (0 or greater)');
      return;
    }

    // Show confirmation modal
    const confirmResult = await showConfirmModal();
    if (!confirmResult.isConfirmed) {
      return;
    }

    setLoading(true);
    showLoadingModal();

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('price', price);
      formData.append('is_free_for_members', isFreeForMembers);
      
      // Append image if selected
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      await createCard(formData);
      
      MySwal.close();
      
      // Show success modal
      showSuccessModal();
      
    } catch (error) {
      MySwal.close();
      console.error('Error adding card:', error);
      
      // Show appropriate error message
      let errorMessage = error.message || 'Failed to add card. Please try again.';
      
      if (error.message.includes('no such table')) {
        errorMessage = `
          Database Error: Table not found.
          
          Steps to fix:
          1. Open terminal in backend directory
          2. Run: python manage.py makemigrations dla_shop
          3. Run: python manage.py migrate
          4. Restart server
        `;
      } else if (error.message.includes('token') || error.message.includes('authentication')) {
        errorMessage = 'Please login first. Your session may have expired.';
      } else if (error.message.includes('Database table not created')) {
        errorMessage = error.message;
      }
      
      showErrorModal(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    MySwal.fire({
      title: <strong>Reset Form?</strong>,
      html: <p>Are you sure you want to clear all fields?</p>,
      icon: 'warning',
      iconColor: '#F59E0B',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, reset!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        setTitle('');
        setDescription('');
        setPrice('0.00');
        setIsFreeForMembers(true);
        setSelectedImage(null);
        setImagePreview(null);
        setError(null);
        if (editor.current) {
          editor.current.value = '';
        }
        MySwal.fire({
          title: <strong>Form Reset!</strong>,
          html: <p>All fields have been cleared.</p>,
          icon: 'success',
          iconColor: '#10B981',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  const isFree = parseFloat(price) === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/30 to-green-50/30">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-emerald-100 px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link 
              to="/dla-shop" 
              className="flex items-center px-4 py-2 hover:bg-emerald-50 rounded-xl transition-colors text-emerald-700"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Reset Form
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Add New Card</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-8 py-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Error Display */}
          {error && (
            <div className="mb-8 bg-red-50 border border-red-200 rounded-2xl p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <span className="text-red-600 text-xl">⚠️</span>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-red-800">Database Setup Required</h3>
                  <div className="mt-2 text-red-700 text-sm whitespace-pre-line">
                    {error}
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`
                          cd C:\\Users\\shafa\\OneDrive\\Desktop\\DLA\\backend
                          python manage.py makemigrations dla_shop
                          python manage.py migrate
                        `);
                        alert('Commands copied to clipboard! Paste in terminal.');
                      }}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                    >
                      Copy Migration Commands
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* LEFT SIDE - Form Section */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Page Title & Description */}
              <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Add New Card</h1>
                <p className="text-gray-600">
                  Create a new card with rich text description. Fill all required fields.
                </p>
              </div>

              {/* Image Upload Section */}
              <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Card Image</h2>
                    <p className="text-sm text-gray-500 mt-1">Upload one image for your card</p>
                  </div>
                  <span className="text-sm font-medium text-emerald-600">* Optional</span>
                </div>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                {imagePreview ? (
                  <div className="relative">
                    <div className="border-2 border-emerald-200 rounded-xl overflow-hidden">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-64 object-cover"
                      />
                    </div>
                    <div className="absolute bottom-4 right-4 flex space-x-2">
                      <button
                        type="button"
                        onClick={triggerFileInput}
                        className="px-4 py-2 bg-white text-emerald-700 border border-emerald-300 rounded-lg hover:bg-emerald-50 transition-colors"
                      >
                        Change Image
                      </button>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={triggerFileInput}
                    className="border-2 border-dashed border-emerald-300 rounded-xl p-12 text-center hover:border-emerald-400 transition-colors cursor-pointer bg-gradient-to-br from-emerald-50/30 to-green-50/30"
                  >
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-emerald-600" />
                    </div>
                    <p className="text-gray-700 font-medium text-lg mb-2">Click to upload image</p>
                    <p className="text-emerald-500">PNG, JPG, GIF • Max 5MB</p>
                  </div>
                )}
              </div>

              {/* Card Details Form */}
              <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm">
                <form onSubmit={handleSubmit} className="space-y-8">
                  
                  {/* Title Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Card Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-lg"
                      placeholder="Enter card title"
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* Rich Text Editor */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Bold className="w-4 h-4" />
                        <Italic className="w-4 h-4" />
                        <Underline className="w-4 h-4" />
                        <span>Rich text editor</span>
                      </div>
                    </div>
                    
                    <div className="border border-gray-300 rounded-xl overflow-hidden">
                      <JoditEditor
                        ref={editor}
                        value={description}
                        config={config}
                        onBlur={newContent => setDescription(newContent)}
                        onChange={newContent => {}}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm text-gray-500">
                        Supports rich text formatting
                      </span>
                      <span className="text-sm text-gray-500">
                        {description.replace(/<[^>]*>/g, '').length}/5000 characters
                      </span>
                    </div>
                  </div>

                  {/* Price Section */}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Price Information <span className="text-red-500">*</span>
                      </label>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Price for Non-Members */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Tag className="w-4 h-4 inline mr-2" />
                            Price for Non-Members
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500">£</span>
                            </div>
                            <input
                              type="text"
                              value={price}
                              onChange={handlePriceChange}
                              className="w-full pl-8 pr-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                              placeholder="0.00"
                              required
                              disabled={loading}
                            />
                          </div>
                          <p className="mt-2 text-sm text-gray-500">
                            Enter 0.00 for FREE products
                          </p>
                        </div>

                        {/* Members Access */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            DLA Members Access
                          </label>
                          <div className="space-y-3">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id="freeForMembers"
                                checked={isFreeForMembers}
                                onChange={(e) => setIsFreeForMembers(e.target.checked)}
                                className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                                disabled={loading}
                              />
                              <label htmlFor="freeForMembers" className="ml-3 text-sm text-gray-700">
                                Free for DLA Members
                              </label>
                            </div>
                            <p className="text-xs text-gray-500">
                              When checked, DLA members get this product for FREE
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Price Preview */}
                    <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4">
                      <h4 className="font-medium text-emerald-800 mb-2">Price Preview</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Non-Members:</span>
                          <span className="font-semibold text-[#b59410]">
                            {isFree ? 'FREE' : `£${parseFloat(price).toFixed(2)}`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">DLA Members:</span>
                          <span className="font-semibold text-green-600">
                            {isFreeForMembers ? 'FREE' : `£${parseFloat(price).toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit & Reset Buttons */}
                  <div className="pt-6 flex space-x-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      disabled={loading}
                      className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all duration-300 flex items-center justify-center text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Reset Form
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium rounded-xl hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                          Adding Card...
                        </>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 mr-3" />
                          Add Card to Collection
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* RIGHT SIDE - Preview Panel */}
            <div className="space-y-8">
              {/* Preview Section */}
              <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm sticky top-24">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Live Preview</h2>
                
                {/* Image Preview Container */}
                {imagePreview && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Image Preview</h3>
                    <div className="border-2 border-emerald-100 rounded-xl overflow-hidden">
                      <img 
                        src={imagePreview} 
                        alt="Card preview" 
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  </div>
                )}

                {/* Card Preview */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Card Preview</h3>
                  <div className="border border-gray-200 rounded-xl p-4 bg-gradient-to-br from-emerald-50/20 to-green-50/20">
                    {title ? (
                      <h4 className="text-lg font-bold text-gray-900 mb-2">{title}</h4>
                    ) : (
                      <h4 className="text-lg font-bold text-gray-400 mb-2">Card Title</h4>
                    )}
                    
                    {description ? (
                      <div 
                        className="text-gray-600 text-sm prose prose-emerald max-w-none"
                        dangerouslySetInnerHTML={{ __html: description.substring(0, 200) + (description.length > 200 ? '...' : '') }}
                      />
                    ) : (
                      <p className="text-gray-400 text-sm">Card description will appear here</p>
                    )}
                    
                    {/* Price Preview in Card */}
                    <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Non-Members:</span>
                        <span className="font-semibold text-[#b59410]">
                          {isFree ? 'FREE' : `£${parseFloat(price).toFixed(2)}`}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">DLA Members:</span>
                        <span className="font-semibold text-green-600">
                          {isFreeForMembers ? 'FREE' : `£${parseFloat(price).toFixed(2)}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Demo Icon */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-3">
                    Card will appear like this in collection
                  </p>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 border border-emerald-200">
                <h3 className="font-semibold text-emerald-800 mb-3">📝 Instructions</h3>
                <ul className="space-y-2 text-sm text-emerald-700">
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Title is required field</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Use rich text editor for formatting</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Enter price for non-members (0.00 for FREE)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Check "Free for DLA Members" if members get it free</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Only one image per card allowed</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-emerald-500 mr-2">•</span>
                    <span>Maximum image size: 5MB</span>
                  </li>
                </ul>
              </div> 
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddCardPage;